import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaPaperPlane,
  FaInstagram
} from 'react-icons/fa';
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  CONTACT_ADDRESS_DISPLAY,
  CONTACT_MAPS_URL,
  CONTACT_INSTAGRAM_URL
} from '../config/contact';

const ContactContainer = styled.section`
  padding: 80px 0;
  background: var(--white-color);
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: var(--secondary-color);
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ContactItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: var(--light-gray);
  border-radius: var(--border-radius);
  transition: var(--transition);
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
    transform: translateY(-2px);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--white-color);
  flex-shrink: 0;
  
  ${ContactItem}:hover & {
    background: var(--white-color);
    color: var(--primary-color);
  }
`;

const ContactDetails = styled.div`
  flex-grow: 1;
`;

const ContactLabel = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
  color: inherit;
`;

const ContactValueLink = styled.a`
  color: inherit;
  opacity: 0.8;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 600;
  color: var(--secondary-color);
`;

const FormInput = styled.input`
  padding: 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FormTextarea = styled.textarea`
  padding: 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SubmitButton = styled(motion.button)`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 15px 30px;
  border-radius: var(--border-radius);
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
    transform: translateY(-2px);
  }
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form gönderme işlemi burada yapılacak
    console.log('Form data:', formData);
    alert('Mesajınız gönderildi! En kısa sürede size dönüş yapacağız.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <ContactContainer>
      <Container>
        <SectionTitle>İletişim</SectionTitle>
        <SectionSubtitle>
          Bizimle iletişime geçin. Size en kısa sürede dönüş yapacağız.
        </SectionSubtitle>
        
        <ContactGrid>
          <ContactInfo>
            <ContactItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <div>
                <ContactLabel>Telefon</ContactLabel>
                <ContactValueLink href={CONTACT_PHONE_TEL}>{CONTACT_PHONE_DISPLAY}</ContactValueLink>
              </div>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <div>
                <ContactLabel>E-posta</ContactLabel>
                <ContactValueLink href={CONTACT_MAILTO}>{CONTACT_EMAIL}</ContactValueLink>
              </div>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <div>
                <ContactLabel>Adres</ContactLabel>
                <ContactValueLink
                  href={CONTACT_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {CONTACT_ADDRESS_DISPLAY}
                </ContactValueLink>
              </div>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <FaInstagram />
              </ContactIcon>
              <div>
                <ContactLabel>Instagram</ContactLabel>
                <ContactValueLink
                  href={CONTACT_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @burdursultanlarbesiciftligi
                </ContactValueLink>
              </div>
            </ContactItem>
          </ContactInfo>
          
          <ContactForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Ad Soyad</FormLabel>
              <FormInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>E-posta</FormLabel>
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Telefon</FormLabel>
              <FormInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Mesajınız</FormLabel>
              <FormTextarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
              Mesaj Gönder
            </SubmitButton>
          </ContactForm>
        </ContactGrid>
      </Container>
    </ContactContainer>
  );
};

export default ContactSection; 